import api from "../../components/api";
import { PropertyGeolocationUpdateRecord } from "./RouteAdd/routeAddModel";

export type Route = {
  name: string
  id: number
  createdAtUtc: string
  updatedAtUtc: string
  active: boolean
  routeType: RoutingType
  crewLeadId: number | null
  crewMemberIds: string | null
  jobsCount: number | null
  estimatedBreakTimeMinutes: number
  dayStartTime: string
  dayEndTime: string
  hasBreakTime: boolean
  defaultScheduleConfig: RoutingScheduleConfig | null
}

export type CrewMember = {
  id: number
  name: string
  avatar: string
  isActive: boolean
  userEmail: string
  hasCustomAvatar?: boolean
  hasStockAvatar?: boolean
  color?: string
}

export type CreateRouteRecordRequest = {
  name: string
  crewLeadId: number | null
  crewMemberIds: string | null
  routeType: RoutingType
  routingScheduleConfig: RoutingScheduleConfig
  dayStartTime: string
  dayEndTime: string
  estimatedBreakTimeMinutes: number
  hasBreakTime: boolean
}

export enum RoutingType {
  OnDemand = 0,
  Planned = 1,
}

export type RoutingScheduleConfig = {
  dayOfWeek: string
  startDate: string
  endDate: string
  repeatUnits: RepeatUnits
  repeatCount: number
  endType: EndType
  occurrences: number
  repeatPlace: string | null
  repeatQuarter: string | null
  repeatMonth: string | null
}

export enum RepeatUnits {
  Day = "day",
  Week = "week",
  Month = "month",
  Year = "year",
}

export enum EndType {
  Never = "never",
  After = "after",
  On = "on",
}

export type RouteSchedule = {
  id: number
  routeId: number
  tenantId: number
  defaultSchedule: boolean
  scheduleConfig: RoutingScheduleConfig
}

export type SalesPerson = {
  displayName: string
  email: string
}

export type AddJobsWorkArea = {
  id: number
  divisionId: number | null
  isApproved: boolean
}

export type AddJobsRecord = {
  jobId: number
  jobName: string
  customerId: number
  customerDisplayName: string
  workareaCount: number
  active: boolean
  allDivisionIds: string
  type: string
  status: string
  salesPersonEmail: string
  address1: string
  address2: string
  city: string
  state: string
  zip: string
  county: string | null
  countryCode: string | null
  nickname: string | null
  propertyId: number
  latitude: number
  longitude: number
  workAreas: AddJobsWorkArea[]
  isSold?: boolean
}

export type RouteJobDetails = {
  jobName: string
  customerDisplayName: string
  address1: string
  address2: string
  city: string
  state: string
  zip: string
  latitude: number
  longitude: number
}

export type RouteJob = {
  id: number
  routeId: number
  jobId: number | null
  tenantLocationId: number | null
  estimatedDurationInMinutes: number
  workAreaIds: string
  tenantId: number
  sortIndex: number
  job?: RouteJobDetails | null
}

export const getRoutesData = async (): Promise<Route[]> => {
  return (await api.get(`/web/routing`)).data
}

export const getRouteById = async (routeId: number): Promise<Route> => {
  return (await api.get(`/web/routing/${routeId}`)).data
}

export const getRouteSchedules = async (): Promise<RouteSchedule[]> => {
  return (await api.get(`/web/routing/schedules`)).data
}

export const getRouteScheduleByRouteId = async (routeId: number): Promise<RouteSchedule[]> => {
    return (await api.get(`/web/routing/schedules/${routeId}`)).data
}

export const saveRouteActive = async (routeId: number, active: boolean): Promise<void> => {
  return (await api.post(`/web/routing/${routeId}/${active}`))
}

export const deleteRoute = async (routeId: number): Promise<void> => {
  return (await api.delete(`/web/routing/${routeId}`))
}

export const duplicateRoute = async (routeId: number): Promise<void> => {
  return (await api.post(`/web/routing/${routeId}/duplicate`))
}

export const loadCrew = async (): Promise<CrewMember[]> => {
  return (await api.get(`/users/crew`)).data
}

export const createRoute = async (request: CreateRouteRecordRequest): Promise<number> => {
  return (await api.post(`/web/routing/create`, request)).data
}

export const getSalesPeople = async (): Promise<SalesPerson[]> => {
  return (await api.get(`/users/sales`)).data
}

export const getJobsForCreateRoute = async (): Promise<AddJobsRecord[]> => {
  return (await api.get(`/web/routing/jobs`)).data
}

export const updateJobPropertyGeolocation = async (request: PropertyGeolocationUpdateRecord): Promise<void> => {
  return (await api.post(`/web/routing/property`, request))
}


export const updateRoute = async (request: Route): Promise<void> => {
  return (await api.post(`/web/routing`, request))
}

export const regenerateRoute = async (routeId: number): Promise<void> => {
  return (await api.post(`/web/routing/regenerate/${routeId}`))
}

export const getRouteJobs = async (routeId: number): Promise<RouteJob[]> => {
  return (await api.get(`/web/routing/route-jobs/${routeId}`)).data
}

export const getAllRouteJobs = async (): Promise<RouteJob[]> => {
  return (await api.get(`/web/routing/route-jobs`)).data
}

export const insertUpdateRouteJobs = async (routeId: number, request: RouteJob[]): Promise<void> => {
  return (await api.post(`/web/routing/route-jobs/${routeId}`, request))
}

export const getProject = async (jobId: number): Promise<any> => {
  return (await api.get(`/jobs/${jobId}`)).data
}

export const updateProject = async (project: any): Promise<void> => {
  return (await api.post(`/jobs`, project))
}

export const deactivateJob = async (jobId: number): Promise<void> => {
  const project = await getProject(jobId)
  project.active = false
  return updateProject(project)
}